// src/app/resume-hero/resume-hero.component.ts
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem
} from '@angular/cdk/drag-drop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription, forkJoin, firstValueFrom } from 'rxjs';

import {
  ResumeRobotService,
  ResumeItem,
  ResumeCategory
} from '../../gen2ai/resume-robot.service';
import {
  ResumeEnhancerService,
  OutputFormat
} from '../../gen2ai/resume-enhancer.service';

import { MatDialog } from '@angular/material/dialog';
import { ResumeDialogViewComponent } from '../dialogs/resume-dialog-view/resume-dialog-view.component';

@Component({
  selector: 'app-resume-hero',
  templateUrl: './resume-hero.component.html',
  styleUrls: ['./resume-hero.component.scss']
})
export class ResumeHeroComponent implements OnInit, OnDestroy {
  @Output() insertIntoChat = new EventEmitter<string>();

  resumeForm: FormGroup;
  searchForm: FormGroup;
  enhanceForm: FormGroup;

  private allResumes: ResumeItem[] = [];

  pinnedResumes: ResumeItem[] = [];
  otherResumes: ResumeItem[] = [];

  selectedResume: ResumeItem | null = null;

  labels: string[] = [];
  selectedLabel: string | null = null;
  showArchived = false;

  readonly primaryCategories: ResumeCategory[] = [
    'Python',
    'AI Dev',
    'Front-end',
    'Angular',
    'Full-stack'
  ];

  /** Job role labels (one of the four) */
  readonly jobRoleLabels: string[] = [
    'Individual Contributor',
    'Senior / Lead',
    'Manager',
    'Director / Executive'
  ];

  // Default navy card color
  readonly defaultCardColor = '#0b1530';

  // IDs used for CDK drag/drop connections (string mode)
  readonly pinnedListId = 'pinnedResumesList';
  readonly otherListId = 'otherResumesList';

  colorPalette: string[] = [
    '#0b1530',
    '#3a420eff',
    '#00e5ff',
    '#ff6f00',
    '#ff4081',
    '#7c4dff',
    '#059440ff',
    '#b37803ff',
    '#19bfbfff'
  ];

  selectedFile: File | null = null;
  isSubmitting = false;
  isEnhancing = false;
  enhanceError: string | null = null;

  private sub = new Subscription();

  constructor(
    private fb: FormBuilder,
    private resumeService: ResumeRobotService,
    private enhancer: ResumeEnhancerService,
    private dialog: MatDialog
  ) {
    this.resumeForm = this.fb.group({
      title: ['', [Validators.required, Validators.maxLength(180)]],
      description: ['', [Validators.maxLength(5000)]],
      primaryCategory: ['AI Dev', Validators.required],
      extraLabels: [''],
      color: [this.defaultCardColor],
      pinned: [false]
    });

    this.searchForm = this.fb.group({
      query: ['']
    });

    // Enhancement form: job description + role + output format
    this.enhanceForm = this.fb.group({
      jobTitle: ['', [Validators.required, Validators.maxLength(200)]],
      jobDescription: ['', [Validators.required, Validators.minLength(30)]],
      jobRoleLabel: [
        'Individual Contributor',
        Validators.required
      ],
      outputFormat: ['docx', Validators.required] // 'docx' | 'pdf' | 'gdoc'
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Lifecycle                                                         */
  /* ------------------------------------------------------------------ */

  ngOnInit(): void {
    this.sub.add(
      this.resumeService.resumes$.subscribe((resumes) => {
        this.allResumes = resumes;
        this.refreshViewFromItems();

        if (this.selectedResume) {
          const updated = resumes.find(
            (r) => r.id === this.selectedResume!.id
          );
          this.selectedResume = updated || null;
        }
      })
    );

    this.sub.add(
      this.searchForm.get('query')!.valueChanges.subscribe(() => {
        this.refreshViewFromItems();
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  /* ------------------------------------------------------------------ */
  /*  Derived UI helpers                                                */
  /* ------------------------------------------------------------------ */

  /** Current upload card color, falls back to default if control is empty */
  get uploadCardColor(): string {
    return this.resumeForm?.value?.color || this.defaultCardColor;
  }

  /* ------------------------------------------------------------------ */
  /*  Upload                                                            */
  /* ------------------------------------------------------------------ */

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];

    if (!file) {
      this.selectedFile = null;
      return;
    }

    const allowedExts = ['.pdf', '.doc', '.docx', '.txt'];
    const lowerName = file.name.toLowerCase();
    const ok = allowedExts.some((ext) => lowerName.endsWith(ext));

    if (!ok) {
      alert('Please select a PDF, DOC, DOCX, or TXT file.');
      input.value = '';
      this.selectedFile = null;
      return;
    }

    this.selectedFile = file;
  }

  get selectedFileName(): string {
    return this.selectedFile
      ? this.selectedFile.name
      : 'Choose a resume (PDF / DOC / DOCX / TXT)…';
  }

  get selectedFileExt(): string {
    if (!this.selectedFile) return '';
    const name = this.selectedFile.name;
    const idx = name.lastIndexOf('.');
    if (idx === -1) return '';
    return name.slice(idx + 1).toUpperCase().slice(0, 4);
  }

  get selectedFileSizeKb(): number {
    if (!this.selectedFile) return 0;
    return Math.max(1, Math.round(this.selectedFile.size / 1024));
  }

  /**
   * Upload + store resume (Firebase).
   * The resume is stored in Firebase Storage, and its download URL is kept in
   * the resume item. Enhancement from the pane uses that stored file URL.
   */
  async createResume(): Promise<void> {
    if (this.resumeForm.invalid || !this.selectedFile) {
      return;
    }

    const { title, description, primaryCategory, extraLabels, color, pinned } =
      this.resumeForm.value;

    const extraLabelList = (extraLabels || '')
      .split(',')
      .map((l: string) => l.trim())
      .filter((l: string) => l.length);

    const payload = {
      title: (title || '').trim(),
      description: (description || '').trim(),
      primaryCategory: primaryCategory as ResumeCategory,
      extraLabels: extraLabelList,
      color: color || this.defaultCardColor,
      pinned
    };

    if (!payload.title && !payload.description) return;

    try {
      this.isSubmitting = true;

      // Create resume and store file in Firebase Storage.
      await this.resumeService.createResume(payload, this.selectedFile);

      this.resumeForm.reset({
        title: '',
        description: '',
        primaryCategory: 'AI Dev',
        extraLabels: '',
        color: this.defaultCardColor,
        pinned: false
      });
      this.selectedFile = null;
    } finally {
      this.isSubmitting = false;
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Filters + view model                                              */
  /* ------------------------------------------------------------------ */

  get filteredPinned(): ResumeItem[] {
    return this.applyFilters(this.pinnedResumes);
  }

  get filteredOthers(): ResumeItem[] {
    return this.applyFilters(this.otherResumes);
  }

  get resume(): ResumeItem | null {
    return this.selectedResume;
  }

  private applyFilters(list: ResumeItem[]): ResumeItem[] {
    let res = [...list];

    if (!this.showArchived) {
      res = res.filter((r) => !r.archived);
    }

    if (this.selectedLabel) {
      res = res.filter((r) => r.labels?.includes(this.selectedLabel!));
    }

    const q: string = this.searchForm.get('query')!.value || '';
    if (q.trim()) {
      const needle = q.toLowerCase();
      res = res.filter(
        (r) =>
          (r.title || '').toLowerCase().includes(needle) ||
          (r.description || '').toLowerCase().includes(needle) ||
          (r.labels || []).some((l) => l.toLowerCase().includes(needle)) ||
          (r.enhancedText || '').toLowerCase().includes(needle)
      );
    }

    return res;
  }

  private refreshViewFromItems(): void {
    this.pinnedResumes = this.allResumes.filter((r) => r.pinned);
    this.otherResumes = this.allResumes.filter((r) => !r.pinned);

    const labelSet = new Set<string>();
    this.allResumes.forEach((r) =>
      (r.labels || []).forEach((l) => labelSet.add(l))
    );
    this.labels = Array.from(labelSet).sort();
  }

  selectLabel(label: string | null): void {
    this.selectedLabel = label;
  }

  toggleArchivedView(): void {
    this.showArchived = !this.showArchived;
  }

  /* ------------------------------------------------------------------ */
  /*  Preview pane helpers                                              */
  /* ------------------------------------------------------------------ */

  selectResume(item: ResumeItem): void {
    this.selectedResume = item;

    const jobTitle =
      item.lastJobTitle || item.title || 'Senior Software Engineer';
    const jobRoleLabel =
      item.lastJobRoleLabel || this.jobRoleLabels[0];

    this.enhanceForm.patchValue({
      jobTitle,
      jobDescription: '',
      jobRoleLabel,
      outputFormat: item.enhancedOutputFormat || 'docx'
    });
    this.enhanceError = null;
  }

  clearPreview(): void {
    this.selectedResume = null;
    this.enhanceError = null;
  }

  /* ------------------------------------------------------------------ */
  /*  Dialog helpers                                                    */
  /* ------------------------------------------------------------------ */

  openResumeDialog(item: ResumeItem): void {
    this.selectResume(item);

    const dialogRef = this.dialog.open(ResumeDialogViewComponent, {
      width: '960px',
      maxWidth: '95vw',
      data: { resume: item },
      autoFocus: false
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      if (result.action === 'useInChat' && result.resume) {
        this.useInChat(result.resume);
      } else if (result.action === 'openFile' && result.resume) {
        this.openResume(result.resume);
      }
    });
  }

  close(): void {
    this.dialog.closeAll();
  }

  /* ------------------------------------------------------------------ */
  /*  Item actions                                                      */
  /* ------------------------------------------------------------------ */

  async togglePinned(item: ResumeItem, pinned: boolean): Promise<void> {
    await this.resumeService.togglePinned(item.id, pinned);
  }

  async archive(item: ResumeItem): Promise<void> {
    await this.resumeService.toggleArchived(item.id, !item.archived);
  }

  async delete(item: ResumeItem): Promise<void> {
    if (confirm('Delete this resume permanently?')) {
      await this.resumeService.deleteResume(item.id);
      if (this.selectedResume?.id === item.id) {
        this.clearPreview();
      }
    }
  }

  async changeColor(item: ResumeItem, color: string): Promise<void> {
    await this.resumeService.updateResume(item.id, { color });
  }

  openResume(item: ResumeItem): void {
    if (!item.fileUrl) return;
    window.open(item.fileUrl, '_blank', 'noopener');
  }

  openFile(): void {
    if (!this.selectedResume?.fileUrl) return;
    window.open(this.selectedResume.fileUrl, '_blank', 'noopener');
  }

  useInChat(item?: ResumeItem): void {
    const target = item ?? this.selectedResume;
    if (!target) return;

    this.selectedResume = target;

    const lines: string[] = [];

    lines.push(`Resume: ${target.title || '(untitled)'}`);
    if (target.primaryCategory) {
      lines.push(`Category: ${target.primaryCategory}`);
    }
    if (target.labels?.length) {
      lines.push(`Labels: ${target.labels.join(', ')}`);
    }
    if (target.description) {
      lines.push('');
      lines.push('Summary notes:');
      lines.push(target.description);
    }
    if (target.enhancedText) {
      lines.push('');
      lines.push('Latest enhanced version (excerpt):');
      lines.push(target.enhancedText);
    }

    this.insertIntoChat.emit(lines.join('\n'));
  }

  /* ------------------------------------------------------------------ */
  /*  Enhancement flow (Gemini-first on backend)                        */
  /* ------------------------------------------------------------------ */

  enhanceForJob(item: ResumeItem): void {
    this.selectResume(item);
  }

  /**
   * Enhancement pipeline:
   * - Builds resumeText from enhancedText or description (if present).
   * - Always sends resumeUrl (Firebase Storage URL) when available.
   * - Calls classic enhanceResume to get text + docx/pdf/gdoc.
   * - Also calls enhanceResumeJson to get structured JSON.
   * - Uses forkJoin to execute both in parallel.
   */
  async submitEnhancement(): Promise<void> {
    if (!this.selectedResume) return;

    if (this.enhanceForm.invalid) {
      this.enhanceForm.markAllAsTouched();
      return;
    }

    const { jobTitle, jobDescription, outputFormat, jobRoleLabel } =
      this.enhanceForm.value;
    const format = outputFormat as OutputFormat;

    const resumeId = this.selectedResume.id;

    // Build the resumeText to send to the Cloud Function.
    // Prefer an existing enhancedText, otherwise fall back to description.
    const resumeText =
      (this.selectedResume.enhancedText || '').trim() ||
      (this.selectedResume.description || '').trim();

    const resumeUrl = this.selectedResume.fileUrl || '';

    // If we literally have neither text nor a file URL, we can't proceed.
    if (!resumeText && !resumeUrl) {
      this.enhanceError =
        'No resume file or base text available for this resume.';
      await this.resumeService.setEnhancementStatus(resumeId, 'error');
      return;
    }

    try {
      this.isEnhancing = true;
      this.enhanceError = null;

      await this.resumeService.setEnhancementStatus(
        resumeId,
        'enhancing'
      );

      const classic$ = this.enhancer.enhanceResume({
        jobTitle,
        jobDescription,
        resumeText,   // may be empty; backend will fall back to resumeUrl
        resumeUrl,
        outputFormat: format,
        jobLabel: jobRoleLabel,
        resumeTitle: this.selectedResume.title,
        candidateHistory: this.selectedResume.description
      });

      const json$ = this.enhancer.enhanceResumeJson({
        jobTitle,
        jobDescription,
        resumeText,   // may be empty; backend will fall back to resumeUrl
        resumeUrl,
        jobRoleLabel,
        resumeTitle: this.selectedResume.title,
        candidateHistory: this.selectedResume.description
      });

      const [classicResult, jsonResult] = await firstValueFrom(
        forkJoin([classic$, json$])
      );

      await this.resumeService.setEnhancementStatus(
        resumeId,
        'ready',
        classicResult.enhancedText,
        jobTitle,
        undefined,
        classicResult.outputFormat as 'docx' | 'pdf' | 'gdoc',
        classicResult.downloadUrl,
        jobRoleLabel,
        jsonResult?.rawModelJson || null
      );
    } catch (err: any) {
      console.error('Enhancement error', err);
      this.enhanceError = err?.message || 'Failed to enhance resume.';
      await this.resumeService.setEnhancementStatus(
        this.selectedResume.id,
        'error'
      );
    } finally {
      this.isEnhancing = false;
    }
  }

  /**
   * Wrapper specifically for the "Generate Resume" button
   * in the submitresume card.
   */
  async submitResume(): Promise<void> {
    await this.submitEnhancement();
  }

  /* ------------------------------------------------------------------ */
  /*  Drag & drop for resume cards                                      */
  /* ------------------------------------------------------------------ */

  dropPinned(ev: CdkDragDrop<ResumeItem[]>): void {
    if (ev.previousContainer === ev.container) {
      moveItemInArray(this.pinnedResumes, ev.previousIndex, ev.currentIndex);
    } else {
      transferArrayItem(
        ev.previousContainer.data,
        ev.container.data,
        ev.previousIndex,
        ev.currentIndex
      );
    }
    void this.persistOrder();
  }

  dropOthers(ev: CdkDragDrop<ResumeItem[]>): void {
    if (ev.previousContainer === ev.container) {
      moveItemInArray(this.otherResumes, ev.previousIndex, ev.currentIndex);
    } else {
      transferArrayItem(
        ev.previousContainer.data,
        ev.container.data,
        ev.previousIndex,
        ev.currentIndex
      );
    }
    void this.persistOrder();
  }

  private async persistOrder(): Promise<void> {
    const orderedIds = [
      ...this.pinnedResumes.map((r) => r.id),
      ...this.otherResumes.map((r) => r.id)
    ];
    await this.resumeService.reorderResumes(orderedIds);
  }
}
